import type { ReactNode } from "react";
import { FormattedMessage } from "react-intl";

import type { BrandAssistantActivityUpdateData } from "@runcheetah/shared";

import { cn } from "@/lib/utils";

const ACTIVITY_KIND_LABELS = {
  research: {
    label: (
      <FormattedMessage id="assistantChat.activity.research" defaultMessage="Researching..." />
    ),
  },
  workspace_lookup: {
    label: <FormattedMessage id="assistantChat.activity.context" defaultMessage="Thinking..." />,
  },
  analysis: {
    label: <FormattedMessage id="assistantChat.activity.analysis" defaultMessage="Analyzing..." />,
  },
  preparing_preview: {
    label: <FormattedMessage id="assistantChat.activity.preview" defaultMessage="Drafting..." />,
  },
  generation: {
    label: <FormattedMessage id="assistantChat.activity.generation" defaultMessage="Drafting..." />,
  },
} satisfies Record<BrandAssistantActivityUpdateData["kind"], { label: ReactNode }>;

export function AssistantLiveActivity({
  updates,
  className,
  label,
}: {
  updates: readonly BrandAssistantActivityUpdateData[];
  className?: string;
  label?: ReactNode;
}) {
  const latestUpdate = updates.at(-1);

  if (!latestUpdate) {
    return null;
  }

  const latestStyle = ACTIVITY_KIND_LABELS[latestUpdate.kind];

  return (
    <div
      className={cn(
        "inline-flex max-w-full items-center text-sm font-semibold leading-6 text-muted-foreground motion-safe:animate-[assistant-memory-fade-in_220ms_ease-out]",
        className,
      )}
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      <span className="bg-[linear-gradient(90deg,var(--muted-foreground)_0%,var(--muted-foreground)_34%,var(--primary)_50%,var(--muted-foreground)_66%,var(--muted-foreground)_100%)] bg-[length:220%_100%] bg-clip-text text-transparent motion-safe:animate-[assistant-status-aurora_2.35s_linear_infinite]">
        {label ?? latestStyle.label}
      </span>
    </div>
  );
}
